import { HOMES_TARGETED_URL_PROPERTY_TYPE, getTargetedUrlPropertyTypeSelection } from 'utils/listing-query-helper';
import { DEFAULT_LISTING_PARAMS_FILTER_SHARED } from 'contexts/preferences/listing-params/defaults';
import type { Filter } from 'contexts/preferences/listing-params/types';

/** Only the fields `generateHeading` reads can change the heading. */
export type HeadingRelevantFilter = Pick<Filter, 'rental' | 'status' | 'homeType'>;

const DEFAULTS = DEFAULT_LISTING_PARAMS_FILTER_SHARED;

/**
 * True when the applied filter says something the answer-style server heading ("Real Estate
 * Property Listings in Toronto, ON") no longer describes, so the classic filter-aware
 * heading ("Houses For Sale in Toronto", "Toronto Recently Sold Condos") should be shown
 * instead.
 *
 * Mirrors the `hasNonDefaultFilters` check the hybrid page's `getServerSideProps` runs
 * against the URL's filter segment, but reads the live client filter: AreaPageV2 applies
 * filters by rewriting the URL in place, without re-running SSR, so the server heading goes
 * stale as soon as the visitor switches Buy/Rent/Sold or picks a home type.
 */
export function hasHeadingRelevantFilter(filter: HeadingRelevantFilter): boolean {
  if (filter.rental !== DEFAULTS.rental) {
    return true;
  }
  if (filter.status !== DEFAULTS.status) {
    return true;
  }
  // The heading only names a home type when the selection narrows to exactly one. Every other
  // combination — all types, none, or a mix — resolves to the same generic "Homes" label the
  // unfiltered heading already uses, so it would not change what the heading says. Asking the
  // resolver the same question `generateHeading` asks keeps this correct for a partial or empty
  // homeType object, which a key-by-key diff against the defaults misreads as "fully narrowed".
  return getTargetedUrlPropertyTypeSelection(filter.homeType) !== HOMES_TARGETED_URL_PROPERTY_TYPE;
}
